import {useState, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {format} from 'date-fns';

import {apiSupportStorage} from '~/modules/firebase';
import {useGetQuestionSampleSetsQuery} from '~/store/rtkQuery';

import {useCreateQuestionSample} from './useCreateQuestionSample';

export const QuestionSampleSets = () => {
  const [showForm, setShowForm] = useState(false);
  const {
    data,
    isLoading,
    error: fetchError,
    refetch,
  } = useGetQuestionSampleSetsQuery();

  const onCreateSuccess = useCallback(() => {
    refetch();
    setShowForm(false);
  }, [refetch]);

  const {
    sampleSize,
    setSampleSize,
    label,
    setLabel,
    canCreate,
    submitting,
    error: createError,
    createSample,
  } = useCreateQuestionSample({onSuccess: onCreateSuccess});

  const downloadStorageFile = useCallback((filePath: string) => {
    const gsReference = apiSupportStorage.refFromURL(filePath);
    gsReference.getDownloadURL();
    gsReference.getDownloadURL().then(url => {
      window.open(url, '_blank');
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-10">
        <span className="loading loading-dots loading-lg"></span>
        <p>Loading</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div role="alert" className="alert alert-error my-4">
        <span>{fetchError.message}</span>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Create New Sample Set
        </button>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Label</th>
            <th>Created At</th>
            <th>Sample Size</th>
            <th>Data File</th>
          </tr>
        </thead>
        <tbody>
          {data?.samples.map(item => (
            <tr key={item.id}>
              <td>{item.id}</td>
              <td>
                <Link
                  className="link"
                  to={`/ai-support/question-sample-sets/${item.id}`}>
                  {item.label}
                </Link>
              </td>
              <td>{format(item.created_at, 'dd MMM yyyy HH:mm')}</td>
              <td>{item.question_ids.length}</td>
              <td>
                <button
                  className="btn"
                  onClick={() => downloadStorageFile(item.data_file_path)}>
                  Download
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={'modal ' + (showForm ? ' modal-open' : '')} role="dialog">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Create Question Sample Set</h3>

          <div>
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Sample Set Label</span>
              </div>
              <input
                className="input input-bordered w-full"
                type="text"
                value={label}
                onChange={e => setLabel(e.target.value)}
              />
            </label>
          </div>

          <div>
            <label className="form-control w-full">
              <div className="label">
                <span className="label-text">Sample Size</span>
              </div>
              <input
                className="input input-bordered w-full"
                type="number"
                value={sampleSize}
                onChange={e => setSampleSize(e.target.value)}
              />
            </label>
          </div>

          {!!createError && (
            <div role="alert" className="alert alert-error my-4">
              <span>{createError.message}</span>
            </div>
          )}

          <div className="modal-action">
            <button
              className="btn"
              type="button"
              onClick={() => setShowForm(false)}>
              Close
            </button>
            <button
              className="btn btn-primary"
              disabled={!canCreate || submitting}
              type="submit"
              onClick={() => createSample()}>
              {submitting ? (
                <span className="loading loading-spinner"></span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
